import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { BackButton } from '../components/ui/back-button';
import { 
  HelpCircle, 
  Plus, 
  Edit, 
  Trash2, 
  GripVertical,
  Image as ImageIcon,
  Link as LinkIcon,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { faqAPI } from '../services/api';
import { sanitizeHtml } from '../utils/sanitize';
import SEO from '../components/SEO';
import { toast } from 'sonner';

// Rich Text Editor Component with proper line breaks and link support
const RichTextEditor = ({ value, onChange, placeholder }) => {
  const editorRef = useRef(null);
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const savedSelectionRef = useRef(null);
  const isInitializedRef = useRef(false);

  // Initialize editor content only once when component mounts
  useEffect(() => {
    if (editorRef.current && !isInitializedRef.current && value) {
      editorRef.current.innerHTML = value;
      isInitializedRef.current = true;
    }
  }, [value]);

  // Reset initialization when value is cleared (for new form)
  useEffect(() => {
    if (!value && editorRef.current) {
      editorRef.current.innerHTML = '';
      isInitializedRef.current = false;
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) {
      const content = editorRef.current.innerHTML;
      // Prevent unnecessary updates
      if (content !== value) {
        onChange(content);
      }
    }
  };

  // Handle Enter key for line breaks
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      document.execCommand('insertHTML', false, '<br><br>');
      handleInput();
    }
  };

  const execCommand = (command, value = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    handleInput();
  };

  // Save current selection before opening dialog
  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      savedSelectionRef.current = selection.getRangeAt(0).cloneRange();
    }
  };

  // Restore selection
  const restoreSelection = () => {
    if (savedSelectionRef.current) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(savedSelectionRef.current);
    }
  };

  const openLinkDialog = () => {
    saveSelection();
    const selection = window.getSelection();
    if (selection && selection.toString()) {
      setLinkText(selection.toString());
    }
    setIsLinkDialogOpen(true);
  };

  const insertLink = () => {
    if (!linkUrl) {
      toast.error('Veuillez entrer une URL');
      return;
    }

    // Focus the editor and restore selection
    editorRef.current?.focus();
    restoreSelection();

    const textToShow = linkText || linkUrl;
    const isExternal = linkUrl.startsWith('http');
    
    // Create the link element
    const linkHtml = isExternal 
      ? `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer" style="color: #c084fc; text-decoration: underline;">${textToShow}</a>&nbsp;`
      : `<a href="${linkUrl}" style="color: #c084fc; text-decoration: underline;">${textToShow}</a>&nbsp;`;

    // Insert the link
    document.execCommand('insertHTML', false, linkHtml);

    // Clean up
    setLinkUrl('');
    setLinkText('');
    setIsLinkDialogOpen(false);
    savedSelectionRef.current = null;
    handleInput();
  };

  return (
    <div className="space-y-0">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-1 p-2 bg-black/50 rounded-t-lg border border-purple-500/30 border-b-0">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => execCommand('bold')}
          className="h-8 px-3 text-purple-300 hover:bg-purple-900/50 font-bold"
          title="Gras"
        >
          B
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => execCommand('italic')}
          className="h-8 px-3 text-purple-300 hover:bg-purple-900/50 italic"
          title="Italique"
        >
          I
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => execCommand('underline')}
          className="h-8 px-3 text-purple-300 hover:bg-purple-900/50 underline"
          title="Souligné"
        >
          U
        </Button>
        <div className="w-px h-6 bg-purple-500/30 mx-1 self-center" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => execCommand('insertUnorderedList')}
          className="h-8 px-2 text-purple-300 hover:bg-purple-900/50"
          title="Liste à puces"
        >
          • Liste
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => execCommand('insertOrderedList')}
          className="h-8 px-2 text-purple-300 hover:bg-purple-900/50"
          title="Liste numérotée"
        >
          1. Liste
        </Button>
        <div className="w-px h-6 bg-purple-500/30 mx-1 self-center" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={openLinkDialog}
          className="h-8 px-2 text-purple-300 hover:bg-purple-900/50"
          title="Insérer un lien"
        >
          <LinkIcon className="h-4 w-4 mr-1" />
          Lien
        </Button>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        className="min-h-[200px] p-4 bg-black/50 border border-purple-500/30 rounded-b-lg text-white focus:outline-none focus:border-purple-400"
        data-placeholder={placeholder}
        style={{ 
          lineHeight: '1.6',
          whiteSpace: 'pre-wrap'
        }}
      />
      
      <p className="text-xs text-gray-500 mt-1">
        Appuyez sur Entrée pour un nouveau paragraphe. Sélectionnez du texte puis cliquez sur "Lien" pour créer un hyperlien.
      </p>

      {/* Link Dialog */}
      <Dialog open={isLinkDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setLinkUrl('');
          setLinkText('');
          savedSelectionRef.current = null;
        }
        setIsLinkDialogOpen(open);
      }}>
          <DialogContent className="bg-black border-purple-500/50">
            <DialogHeader>
              <DialogTitle className="text-purple-300">Insérer un lien</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-purple-300">URL du lien</Label>
                <Input
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://exemple.com ou /page"
                  className="bg-black/50 border-purple-500/30 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-purple-300">Texte à afficher (optionnel)</Label>
                <Input
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                  placeholder="Cliquez ici..."
                  className="bg-black/50 border-purple-500/30 text-white"
                />
                <p className="text-xs text-gray-500">
                  Laissez vide pour utiliser le texte sélectionné ou l'URL
                </p>
              </div>
              <Button onClick={insertLink} className="w-full bg-purple-600 hover:bg-purple-500">
                Insérer le lien
              </Button>
            </div>
          </DialogContent>
        </Dialog>
    </div>
  );
};

const FAQ = () => {
  const [faqItems, setFaqItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    image_url: ''
  });
  const { isAdmin } = useAuth();

  useEffect(() => {
    loadFAQ();
  }, []);

  // Scroll to the question targeted by the URL hash (e.g. from global search)
  useEffect(() => {
    if (loading || !window.location.hash) return;
    const target = document.getElementById(window.location.hash.slice(1));
    if (target) {
      setTimeout(() => target.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  }, [loading]);

  const loadFAQ = async () => {
    try {
      setLoading(true);
      const data = await faqAPI.getAll();
      setFaqItems(data);
    } catch (error) {
      console.error('Error loading FAQ:', error);
      toast.error('Erreur lors du chargement de la FAQ');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      // Validate form data
      if (!formData.question.trim()) {
        toast.error('La question est requise');
        return;
      }
      if (!formData.answer.trim()) {
        toast.error('La réponse est requise');
        return;
      }
      
      await faqAPI.create({
        question: formData.question.trim(),
        answer: formData.answer,
        image_url: formData.image_url?.trim() || null
      });
      toast.success('Question ajoutée !');
      setIsDialogOpen(false);
      resetForm();
      loadFAQ();
    } catch (error) {
      console.error('Error creating FAQ:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      // Validate form data
      if (!formData.question.trim()) {
        toast.error('La question est requise');
        return;
      }
      if (!formData.answer.trim()) {
        toast.error('La réponse est requise');
        return;
      }
      
      await faqAPI.update(editingItem, {
        question: formData.question.trim(),
        answer: formData.answer,
        image_url: formData.image_url?.trim() || null
      });
      toast.success('Question modifiée !');
      setIsDialogOpen(false);
      setEditingItem(null);
      resetForm();
      loadFAQ();
    } catch (error) {
      console.error('Error updating FAQ:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette question ?')) return;
    
    try {
      await faqAPI.delete(itemId);
      toast.success('Question supprimée');
      loadFAQ();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const openEditDialog = (item) => {
    setEditingItem(item.item_id);
    setFormData({
      question: item.question,
      answer: item.answer,
      image_url: item.image_url || ''
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      question: '',
      answer: '',
      image_url: ''
    });
    setEditingItem(null);
  };

  const scrollToQuestion = (itemId) => {
    const element = document.getElementById(`faq-${itemId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Generate slug from question for anchor
  const generateSlug = (question) => {
    return question
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .substring(0, 50);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 px-4 pb-12 flex items-center justify-center">
        <div className="text-purple-400 text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 px-4 pb-12">
      <SEO title="FAQ" description="Questions fréquentes sur l'association Just Rôle It : adhésion, séances de jeu de rôle, fonctionnement." path="/faq" />
      {/* Global styles for FAQ answers - only rendered once */}
      <style>{`
        .faq-answer a {
          color: #c084fc !important;
          text-decoration: underline !important;
          cursor: pointer !important;
        }
        .faq-answer a:hover {
          color: #d8b4fe !important;
        }
        .faq-answer strong {
          color: #d8b4fe;
        }
        .faq-answer ul {
          list-style-type: disc;
          padding-left: 1.5rem;
          margin: 0.5rem 0;
        }
        .faq-answer ol {
          list-style-type: decimal;
          padding-left: 1.5rem;
          margin: 0.5rem 0;
        }
        .faq-answer li {
          margin: 0.25rem 0;
        }
      `}</style>
      
      <div className="container mx-auto max-w-4xl">
        <BackButton />
        
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-purple-600 mb-2 tracking-wide">
              Questions Fréquentes
            </h1>
            <p className="text-gray-400">Tout ce que vous devez savoir sur Just Rôle It</p>
          </div>
          
          {isAdmin() && (
            <Dialog open={isDialogOpen} onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 neon-button"
                  data-testid="add-faq-btn"
                >
                  <Plus className="mr-2 h-5 w-5" />
                  Ajouter une question
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-gradient-to-br from-purple-900/95 to-black border-2 border-purple-500/50 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold text-purple-300">
                    {editingItem ? 'Modifier la question' : 'Ajouter une question'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={editingItem ? handleUpdate : handleCreate} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-purple-300">Question</Label>
                    <Input
                      value={formData.question}
                      onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                      className="bg-black/50 border-purple-500/30 text-white"
                      placeholder="Ex: Comment rejoindre l'association ?"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-purple-300">Réponse</Label>
                    <RichTextEditor
                      value={formData.answer}
                      onChange={(value) => setFormData({ ...formData, answer: value })}
                      placeholder="Écrivez votre réponse ici..."
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-purple-300 flex items-center">
                      <ImageIcon className="h-4 w-4 mr-2" />
                      Image (optionnel)
                    </Label>
                    <Input
                      value={formData.image_url}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      className="bg-black/50 border-purple-500/30 text-white"
                      placeholder="https://exemple.com/image.jpg"
                    />
                    {formData.image_url && (
                      <div className="mt-2">
                        <img 
                          src={formData.image_url} 
                          alt="Aperçu" 
                          className="max-h-32 rounded-lg border border-purple-500/30"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      </div>
                    )}
                  </div>
                  
                  <Button type="submit" className="w-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 neon-button">
                    {editingItem ? 'Modifier' : 'Ajouter'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Table of Contents */}
        {faqItems.length > 0 && (
          <Card className="bg-gradient-to-br from-purple-900/30 to-black border border-purple-500/30 p-6 mb-8">
            <h2 className="text-lg font-bold text-purple-300 mb-4 flex items-center">
              <HelpCircle className="h-5 w-5 mr-2" />
              Sommaire
            </h2>
            <nav className="space-y-2">
              {faqItems.map((item, index) => (
                <button
                  key={item.item_id}
                  onClick={() => scrollToQuestion(item.item_id)}
                  className="flex items-center text-left w-full text-gray-300 hover:text-purple-400 transition-colors group"
                  data-testid={`faq-toc-${index}`}
                >
                  <ChevronRight className="h-4 w-4 mr-2 text-purple-500 group-hover:translate-x-1 transition-transform" />
                  <span className="text-purple-400 mr-2">{index + 1}.</span>
                  {item.question}
                </button>
              ))}
            </nav>
          </Card>
        )}

        {/* FAQ Items */}
        <div className="space-y-6">
          {faqItems.length === 0 ? (
            <Card className="bg-gradient-to-br from-purple-900/40 to-black border-2 border-purple-500/30 p-12 text-center">
              <HelpCircle className="h-16 w-16 text-purple-500/50 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">Aucune question pour le moment</p>
              {isAdmin() && (
                <p className="text-gray-500 text-sm mt-2">
                  Cliquez sur "Ajouter une question" pour commencer
                </p>
              )}
            </Card>
          ) : (
            faqItems.map((item, index) => (
              <Card 
                key={item.item_id}
                id={`faq-${item.item_id}`}
                className="bg-gradient-to-br from-purple-900/40 to-black border-2 border-purple-500/30 p-6 hover:border-purple-400/50 transition-all scroll-mt-24"
                data-testid={`faq-item-${index}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    {/* Question */}
                    <h3 className="text-xl font-bold text-purple-300 mb-4 flex items-start">
                      <span className="text-purple-500 mr-3 text-2xl">{index + 1}.</span>
                      {item.question}
                    </h3>
                    
                    {/* Image */}
                    {item.image_url && (
                      <div className="mb-4">
                        <img 
                          src={item.image_url} 
                          alt={item.question}
                          className="max-w-full md:max-w-md rounded-lg border border-purple-500/30 shadow-lg"
                        />
                      </div>
                    )}
                    
                    {/* Answer */}
                    <div 
                      className="text-gray-300 faq-answer"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.answer) }}
                    />
                  </div>
                  
                  {/* Admin Actions */}
                  {isAdmin() && (
                    <div className="flex flex-col gap-2 flex-shrink-0">
                      <Button
                        onClick={() => openEditDialog(item)}
                        variant="outline"
                        size="sm"
                        className="border-purple-500/50 text-purple-400 hover:bg-purple-900/30"
                        data-testid={`edit-faq-${index}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        onClick={() => handleDelete(item.item_id)}
                        variant="outline"
                        size="sm"
                        className="border-red-500/50 text-red-400 hover:bg-red-900/30"
                        data-testid={`delete-faq-${index}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default FAQ;
